import { StandardEvents } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

let localCartWrite = 0;
let externalRefreshTimer = 0;

const isCartMutation = (input, init) => {
  const url = input instanceof Request ? input.url : String(input || '');
  const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
  return method !== 'GET' && /\/cart\/(?:add|change|update)(?:\.js)?(?:\?|$)/.test(url);
};

const scheduleExternalRefresh = () => {
  window.clearTimeout(externalRefreshTimer);
  externalRefreshTimer = window.setTimeout(() => {
    const cart = document.querySelector('store-mini-cart');
    if (cart instanceof StoreMiniCart) cart.refreshAfterExternalCartChange();
  }, 350);
};

const installCartWatch = () => {
  if (window.__storeMiniCartWatch) return;
  window.__storeMiniCartWatch = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    if (localCartWrite === 0 && isCartMutation(input, init)) scheduleExternalRefresh();
    return response;
  };
};

const trackedFetch = async (url, options) => {
  localCartWrite += 1;
  try {
    return await fetch(url, options);
  } finally {
    localCartWrite -= 1;
  }
};

const withJs = (url, fallback) => {
  const value = url || fallback;
  return value.endsWith('.js') ? value : `${value}.js`;
};

const changeUrl = () => withJs(window.Theme?.routes?.cart_change_url, '/cart/change');
const addUrl = () => withJs(window.Theme?.routes?.cart_add_url, '/cart/add');

class StoreMiniCart extends HTMLElement {
  /** @type {number} */
  #requestId = 0;

  /** @type {number} */
  #qtyTimer = 0;

  /** @type {number} */
  #externalTimer = 0;

  /** @type {boolean} */
  #closing = false;

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.querySelector('dialog');
  }

  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.#dialog?.addEventListener('cancel', this.#onCancel);
    this.#dialog?.addEventListener('click', this.#onBackdrop);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('submit', this.#onDocumentSubmit);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#syncCount(Number(this.dataset.itemCount || '0'));
  }

  disconnectedCallback() {
    this.#dialog?.removeEventListener('cancel', this.#onCancel);
    this.#dialog?.removeEventListener('click', this.#onBackdrop);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('submit', this.#onDocumentSubmit);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    window.clearTimeout(this.#qtyTimer);
    window.clearTimeout(this.#externalTimer);
    unlockScroll(this);
    this.dataset.ready = 'false';
  }

  refreshAfterExternalCartChange() {
    window.clearTimeout(this.#externalTimer);
    this.#externalTimer = window.setTimeout(() => {
      const requestId = ++this.#requestId;
      this.#renderSection(requestId).catch(() => {
        if (requestId === this.#requestId) this.#showError(this.dataset.error || '');
      });
    }, 0);
  }

  open() {
    const dialog = this.#dialog;
    if (!dialog || dialog.open || this.hasAttribute('data-prevent-open')) return;

    this.#closing = false;
    dialog.showModal();
    // showModal alone is unreliable on iOS Safari; use theme scroll-lock.
    lockScroll(this);
    document.body.classList.add('store-mini-cart-open');
    this.#syncExpanded(true);
    window.requestAnimationFrame(() => {
      dialog.classList.add('is-open');
      this.querySelector('.store-mini-cart__title')?.focus();
    });
  }

  close() {
    const dialog = this.#dialog;
    if (!dialog?.open || this.#closing) return;

    this.#closing = true;
    dialog.classList.remove('is-open');

    const finish = () => {
      if (!this.#closing) return;
      this.#closing = false;
      if (dialog.open) dialog.close();
      document.body.classList.remove('store-mini-cart-open');
      unlockScroll(this);
      this.#syncExpanded(false);
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    dialog.addEventListener(
      'transitionend',
      (event) => {
        if (event.target === dialog && event.propertyName === 'transform') finish();
      },
      { once: true }
    );
    window.setTimeout(finish, 280);
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const node = this.querySelector('[data-store-mini-cart-error]');
    if (!(node instanceof HTMLElement)) return;
    node.textContent = message || '';
    node.hidden = !message;
  }

  /**
   * @param {boolean} loading
   */
  #setLoading(loading) {
    this.classList.toggle('is-loading', loading);
    const spinner = this.querySelector('[data-store-mini-cart-spinner]');
    if (spinner instanceof HTMLElement) spinner.hidden = !loading;
  }

  /**
   * @param {number} count
   */
  #syncCount(count) {
    const label = this.dataset.countLabel || '';
    document.querySelectorAll('[data-store-header-cart-count]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.textContent = String(count);
      node.hidden = count < 1;
    });
    document.querySelectorAll('[data-store-header-cart-count-text]').forEach((node) => {
      node.textContent = count > 0 ? `${label}: ${count}` : '';
    });
  }

  /**
   * @param {boolean} expanded
   */
  #syncExpanded(expanded) {
    document.querySelectorAll('[data-store-mini-cart-open]').forEach((node) => {
      node.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  /**
   * Product pages answer `Accept: application/json` with the product Ajax
   * payload, which drops `?sections=`. Ask for any response type instead.
   * @param {number} requestId
   * @param {string} [html]
   */
  async #renderSection(requestId, html) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) throw new Error('section');

    const active = document.activeElement;
    const lineKey = active instanceof HTMLInputElement ? active.dataset.lineKey || '' : '';

    let sectionHtml = html;
    if (typeof sectionHtml !== 'string') {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionId);
      const response = await fetch(url, {
        headers: { Accept: '*/*', 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('section');
      if (requestId !== this.#requestId) return;
      const data = await response.json();
      sectionHtml = data[sectionId];
    }

    if (requestId !== this.#requestId) return;
    if (typeof sectionHtml !== 'string') throw new Error('section');
    this.#applySectionHtml(sectionHtml, lineKey);
  }

  /**
   * @param {string} html
   * @param {string} [lineKey]
   */
  #applySectionHtml(html, lineKey = '') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('store-mini-cart');
    if (!next) throw new Error('section');

    const widgetSelector = '.seal-widget, .seal-widget-standard, .seal-widget-checkout-plus-container';
    const widgets = [...this.querySelectorAll(widgetSelector)].filter(
      (node) => !node.parentElement?.closest(widgetSelector)
    );
    const nextBody = next.querySelector('[data-store-mini-cart-body]');
    const nextFooter = next.querySelector('[data-store-mini-cart-footer]');
    const body = this.querySelector('[data-store-mini-cart-body]');
    const footer = this.querySelector('[data-store-mini-cart-footer]');
    if (body && nextBody) body.replaceWith(nextBody);
    if (footer && nextFooter) footer.replaceWith(nextFooter);

    const anchor = this.querySelector('.store-mini-cart__seel-anchor .button-group');
    if (anchor) widgets.forEach((node) => anchor.insertAdjacentElement('beforebegin', node));

    const count = Number(next.dataset.itemCount || '0');
    this.dataset.itemCount = String(count);
    this.#syncCount(count);

    if (!lineKey || typeof CSS === 'undefined' || !CSS.escape) return;
    const input = this.querySelector(`[data-store-mini-cart-qty][data-line-key="${CSS.escape(lineKey)}"]`);
    if (input instanceof HTMLElement) input.focus();
  }

  #sectionRequestFields() {
    const sections = this.dataset.sectionId;
    if (!sections) return {};
    return { sections, sections_url: window.location.pathname };
  }

  async #refreshAndOpen() {
    const requestId = ++this.#requestId;
    this.#showError('');
    this.#setLoading(true);
    try {
      await this.#renderSection(requestId);
      if (requestId !== this.#requestId) return;
      this.#setLoading(false);
      this.open();
    } catch {
      if (requestId === this.#requestId) this.#showError(this.dataset.error || '');
    } finally {
      if (requestId === this.#requestId) this.#setLoading(false);
    }
  }

  /**
   * @param {string} key
   * @param {number} quantity
   */
  async #changeLine(key, quantity) {
    const requestId = ++this.#requestId;
    this.#setLoading(true);
    this.#showError('');

    try {
      const response = await trackedFetch(changeUrl(), {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ id: key, quantity, ...this.#sectionRequestFields() }),
      });
      const json = await response.json();
      if (requestId !== this.#requestId) return;
      if (!response.ok || json.status) {
        this.#showError(json.description || json.message || this.dataset.error || '');
      }
      if (Number.isFinite(json.item_count)) this.#syncCount(json.item_count);
      const html = json.sections?.[this.dataset.sectionId || ''];
      if (typeof html === 'string') this.#applySectionHtml(html);
      else await this.#renderSection(requestId);
    } catch {
      if (requestId === this.#requestId) this.#showError(this.dataset.error || '');
    } finally {
      if (requestId === this.#requestId) this.#setLoading(false);
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  async #addFromForm(form) {
    const button = form.querySelector('[type="submit"]');
    const id = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    if (!(id instanceof HTMLInputElement)) return;

    if (button instanceof HTMLButtonElement) button.disabled = true;
    const quantity = Number(quantityInput instanceof HTMLInputElement ? quantityInput.value : '1') || 1;

    try {
      const response = await trackedFetch(addUrl(), {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ id: Number(id.value), quantity, ...this.#sectionRequestFields() }),
      });
      const json = await response.json();
      if (!response.ok || json.status) {
        this.#showError(json.description || json.message || this.dataset.error || '');
        this.open();
        return;
      }
      const html = json.sections?.[this.dataset.sectionId || ''];
      if (typeof html === 'string') {
        this.#applySectionHtml(html);
        this.open();
        return;
      }
      await this.#refreshAndOpen();
    } catch {
      this.#showError(this.dataset.error || '');
      this.open();
    } finally {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} delta
   */
  #stepQuantity(input, delta) {
    const min = Number(input.min || '1');
    const next = Math.max(min, Number(input.value || min) + delta);
    input.value = String(next);
    this.#queueQuantity(input);
  }

  /**
   * @param {HTMLInputElement} input
   */
  #queueQuantity(input) {
    const key = input.dataset.lineKey;
    if (!key) return;
    window.clearTimeout(this.#qtyTimer);
    this.#qtyTimer = window.setTimeout(() => {
      this.#changeLine(key, Number(input.value || '1'));
    }, 400);
  }

  /**
   * @param {Event} event
   */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * @param {MouseEvent} event
   */
  #onBackdrop = (event) => {
    if (event.target === this.#dialog) this.close();
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-store-mini-cart-close]')) {
      this.close();
      return;
    }

    const remove = target.closest('[data-store-mini-cart-remove]');
    if (remove instanceof HTMLElement && remove.dataset.lineKey) {
      event.preventDefault();
      this.#changeLine(remove.dataset.lineKey, 0);
      return;
    }

    const minus = target.closest('[data-store-mini-cart-qty-minus]');
    const plus = target.closest('[data-store-mini-cart-qty-plus]');
    const step = minus || plus;
    if (!(step instanceof HTMLElement)) return;
    const input = step.parentElement?.querySelector('[data-store-mini-cart-qty]');
    if (input instanceof HTMLInputElement) this.#stepQuantity(input, minus ? -1 : 1);
  };

  /**
   * Enter in a quantity field updates that line. It does not submit checkout.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Enter') return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-store-mini-cart-qty]')) return;
    event.preventDefault();
    this.#queueQuantity(target);
  };

  /**
   * @param {Event} event
   */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-store-mini-cart-qty]')) return;
    this.#queueQuantity(target);
  };

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('[data-store-mini-cart-open]')) return;
    if (this.#dialog?.open) this.close();
    else this.open();
  };

  /**
   * Product cards post to the cart by default. Keep that path without JS,
   * and open this drawer when the add succeeds.
   * @param {SubmitEvent} event
   */
  #onDocumentSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.store-product-card__form')) return;
    event.preventDefault();
    this.#addFromForm(form);
  };

  /**
   * Product detail add-to-cart dispatches this after `/cart/add.js`.
   * Dynamic checkout buttons do not, so they still leave the page.
   * @param {Event & { action?: string, promise?: Promise<{ detail?: { didError?: boolean } }> }} event
   */
  #onCartLinesUpdate = (event) => {
    const action = event.action || /** @type {{ action?: string }} */ (event.detail)?.action;
    if (action !== 'add' || !event.promise) return;

    event.promise
      .then((result) => {
        if (result?.detail?.didError) return;
        const count = result?.cart?.totalQuantity ?? result?.detail?.itemCount;
        if (Number.isFinite(count)) this.#syncCount(count);
        const html = result?.detail?.sections?.[this.dataset.sectionId || ''];
        if (typeof html === 'string') {
          try {
            this.#applySectionHtml(html);
            this.open();
            return;
          } catch {
            // The cart response can omit a usable section. Fetch it next.
          }
        }
        this.#refreshAndOpen();
      })
      .catch(() => {});
  };
}

if (!customElements.get('store-mini-cart')) {
  customElements.define('store-mini-cart', StoreMiniCart);
}

installCartWatch();
